/**
 * Admin module constants — option lists for form selects/filters and shared
 * presentation helpers. Centralized so every admin page offers identical
 * choices and labels.
 */

/** Grades a student can be in. */
export const GRADES = [
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
];

/** Subjects mentors teach / classes cover. */
export const SUBJECTS = [
  'Python Level-1',
  'Python Level-2',
  'Python Level-3',
  'Web Dev Level-1',
  'Web Dev Level-2',
  'Web Dev Level-3',
  'Java Level-1',
  'Java Level-2',
  'Java Level-3',
  'C++ Level-1',
  'C++ Level-2',
  'C++ Level-3',
  'Machine Learning Level-1',
  'Machine Learning Level-2',
  'Machine Learning Level-3',
  'AI Powered Web Dev Level-1',
  'AI Powered Web Dev Level-2',
  'AI Powered Web Dev Level-3',
  'AI Powered Python Level-1',
  'AI Powered Python Level-2',
  'AI Powered Python Level-3',
  'Scratch Level-1',
  'Scratch Level-2',
  'Scratch Level-3',
  'Robotics Level-1',
  'Robotics Level-2',
  'Robotics Level-3',
  'Deep Learning Level-1',
  'Deep Learning Level-2',
  'Deep Learning Level-3',
  'Game Development Level-1',
  'Game Development Level-2',
  'Game Development Level-3',
  'Block Codes Level-1',
  'Block Codes Level-2',
  'Block Codes Level-3',
];

/** Difficulty levels a course can carry. */
export const COURSE_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

/** Enrollment lifecycle — mirrors ENROLLMENT_STATUS in the service. */
export const ENROLLMENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Badge variant per enrollment status (matches the Badge component). */
export const ENROLLMENT_STATUS_VARIANT = {
  active: 'success',
  paused: 'warning',
  completed: 'brand',
  cancelled: 'danger',
};

export const ENROLLMENT_STATUS_LABEL = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** A pragmatic country list (extend as needed). */
export const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'India',
  'Singapore', 'United Arab Emirates', 'Germany', 'France', 'Spain',
  'Netherlands', 'Brazil', 'Mexico', 'Japan', 'South Africa', 'Nigeria',
  'Kenya', 'New Zealand', 'Ireland', 'Switzerland',
];

/** IANA-ish timezone labels covering the common student/mentor regions. */
export const TIMEZONES = [
  'UTC',
  'America/Los_Angeles (PT)',
  'America/Denver (MT)',
  'America/Chicago (CT)',
  'America/New_York (ET)',
  'America/Sao_Paulo (BRT)',
  'Europe/London (GMT)',
  'Europe/Paris (CET)',
  'Africa/Lagos (WAT)',
  'Africa/Johannesburg (SAST)',
  'Asia/Dubai (GST)',
  'Asia/Kolkata (IST)',
  'Asia/Singapore (SGT)',
  'Asia/Tokyo (JST)',
  'Australia/Sydney (AEST)',
];

/** Class durations offered in the scheduler (minutes). */
export const DURATIONS = [
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

/** Rows-per-page choices for tables. */
export const PAGE_SIZES = [10, 25, 50];

/**
 * Badge variant + label for each class status. These two maps are what
 * `StatusBadge` renders, so they are the single place a class status gets its
 * wording across the student, mentor and admin surfaces alike.
 */
export const STATUS_VARIANT = {
  scheduled: 'brand',
  completed: 'success',
  cancelled: 'danger',
  // A mentor no-show costs the mentor ₹500, so it reads as an alert rather than
  // a caution — it is the one outcome here that someone has to answer for.
  mentor_no_show: 'danger',
  // Neutral: nobody turned up, nobody is charged, nothing needs actioning.
  both_no_show: 'neutral',
  not_conducted: 'warning',
  student_no_show: 'warning',
};

export const STATUS_LABEL = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  // The mentor never joined (or joined >20 min late).
  mentor_no_show: 'Mentor no-show',
  // Neither side joined. No penalty — see CLASS_STATUS.BOTH_NO_SHOW.
  both_no_show: 'Nobody joined',
  // The mentor WAS there and reported the session couldn't run. Distinct from
  // `mentor_no_show`, which this label used to cover as well.
  not_conducted: 'Not conducted',
  student_no_show: 'Student no-show',
};
